import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { PublicationNewAliasRequest } from '@/module/Author/Publication/model/PublicationTypes';
import { TextFilters } from '@/module/Author/Text/model/TextTypes';

import { Id, PagedParams, PagedResponse, getQueryKey, invalidateAfterDelete } from '@feature/utils';

import * as api from './ProceedingApi';
import { Proceeding, ProceedingRequest } from './ProceedingTypes';

const proceedingKey = getQueryKey('proceeding');

export function getProceedingPagedOptions(
  params: PagedParams<TextFilters>,
): UseSuspenseQueryOptions<PagedResponse<Proceeding>> {
  return {
    queryKey: proceedingKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getProceedingOptions(proceedingId: Id): UseSuspenseQueryOptions<Proceeding> {
  return {
    queryKey: proceedingKey.one(proceedingId),
    queryFn: () => api.get(proceedingId),
  };
}

export function useProceedingEdit(proceedingId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ProceedingRequest) => api.edit(proceedingId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: proceedingKey.root(),
      }),
  });
}

export function useProceedingCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ProceedingRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: proceedingKey.root(),
      }),
  });
}

export function useProceedingDelete(proceedingId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(proceedingId),
    onSuccess: () => invalidateAfterDelete(queryClient, proceedingKey, proceedingId),
  });
}

export function useProceedingAddNewAlias() {
  const queryClient = useQueryClient();
  return useMutation<Proceeding, Error, { proceedingId: Id; request: PublicationNewAliasRequest }>({
    mutationFn: ({ proceedingId, request }) => api.addNewAlias(proceedingId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: proceedingKey.root(),
      }),
  });
}
